/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { CREATE_BOX } from "../graphql/createBox.mutation";
import { CREATE_ORDER } from "../graphql/createOrder.mutation";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { GET_CURRENCY_EXCHANGE_RATE } from "../graphql/getCurrencyExchangeRate.query";
import { REGISTER_PAYMENT } from "../graphql/registerPayment.mutation";
import { PAY_FOR_TICKETS } from "../graphql/payForTickets.mutation";
import { PAY_COSTUME_AND_TICKETS } from "../graphql/payCostumeAndTickets.mutation";
import { CHECK_PAYMENT_FROM_FAC } from "../graphql/checkoutFAC.mutation";
import { HOLD_TICKETS } from "../graphql/holdTickets.mutation";
import { parseTicketError } from "../../../helpers/ticketError.helper";

const is = require("is_js");
export interface ICreateBoxArgs {
  presentationID: string;
  sectionID: string;
  email: string;
  name: string;
  mode: string;
  price: any;
  currency: string;
  platformFee?: number;
  items: {
    costumeType: string;
    costumeFeature: string;
    costumeAttribute: string;
    costumeValue: string;
  }[];
  onlyCreateBox?: boolean;
  skipPaymentDispatch?: boolean;
  boxId?: string;
  existingBox?: any;
  payOrder?: string;
  payOrderId?: string;
  ticketOrderId?: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface ICreateBoxTypeMethods extends IPluginMethodMap {
  createBox(args: any): Promise<any>;
  createBoxRecord(args: any): Promise<any>;
}

/**
 * Holds the selected tickets on the Box (one fete per TicketOrder). Returns the
 * held TicketOrder, or null when no tickets are selected. Throws ticket errors
 * (e.g. SOLD_OUT) so the caller can send the user back to the ticket step.
 */
const holdSelectedTickets = async (
  client: any,
  resolvedBoxId: string | undefined,
  args: any
): Promise<any> => {
  const costumeSrv = (() => {
    try {
      const app = VApplication.getInstance();
      return app.getSync("services.CostumePageService") as any;
    } catch {
      return null;
    }
  })();
  const ticketList =
    args?.tickets || args?.event?.tickets || costumeSrv?.getSelectedTickets() || [];
  if (!ticketList || ticketList.length === 0 || !resolvedBoxId) return null;
  try {
    const ticketFeteId = ticketList[0]?.feteId || ticketList[0]?.eventId;
    // One TicketOrder covers one fete; send the tierId from getTicketFetes as is.
    const ticketLines = ticketList
      .filter((t: any) => (t.feteId || t.eventId) === ticketFeteId)
      .map((t: any) => ({
        tierId: t.tierId,
        quantity: Number(t.quantity) || 1,
      }));
    if (ticketLines.some((l: any) => !l.tierId)) {
      throw new Error("INVALID_REQUEST: Please reselect your tickets and try again.");
    }
    // Stable for this Box + selection, so a retry returns the same hold
    // instead of creating a second one.
    const holdKey = `hold_${resolvedBoxId}_${ticketFeteId}_${ticketLines
      .map((l: any) => `${l.tierId}x${l.quantity}`)
      .join("-")}`;
    const holdRes = await client.mutate({
      mutation: HOLD_TICKETS,
      variables: {
        data: {
          boxId: resolvedBoxId,
          feteId: ticketFeteId,
          lines: ticketLines,
          idempotencyKey: holdKey,
        },
      },
      fetchPolicy: "no-cache",
    });
    const order = holdRes?.data?.holdTickets;
    if (order) {
      if (typeof window !== "undefined" && window?.sessionStorage) {
        window.sessionStorage.setItem("gems_active_ticket_order", JSON.stringify(order));
        window.sessionStorage.setItem("gems_ticket_order_id", order.ticketOrderId || order.id);
      }
      if (costumeSrv) {
        costumeSrv.setActiveTicketOrder(order);
      }
    }
    return order || null;
  } catch (autoHoldErr: any) {
    console.error("Auto holdTickets after createBox error:", autoHoldErr?.message || autoHoldErr);
    throw autoHoldErr;
  }
};

export class CreateBoxService implements IAgentPlugin {
  readonly methods: ICreateBoxTypeMethods;

  constructor() {
    this.methods = {
      createBox: this.createBox.bind(this),
      createBoxRecord: this.createBoxRecord.bind(this),
    };
  }

  async createBoxRecord(args: any) {
    return await this.createBox({ ...args, onlyCreateBox: true });
  }

  async createBox(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const storageService = StorageModule.getInstance();
    console.log("57print", args);
    let items = args?.items;
    if (items instanceof Map) {
      const arr: any[] = [];
      items.forEach((val: any) => {
        if (val && !val.soldOut) {
          arr.push({
            costumeType: val?.costumeType || val?.costumeTypeID,
            costumeFeature: val?.feature?.id || val?.costumeFeature,
            costumeAttribute: val?.attribute?.id || val?.costumeAttribute,
            costumeValue: val?.value?.id || val?.costumeValue,
          });
        }
      });
      items = arr;
    } else if (items && typeof items.forEach === "function" && !Array.isArray(items)) {
      const arr: any[] = [];
      items.forEach((val: any) => arr.push(val));
      items = arr;
    }

    let variables: any = {
      data: {
        presentation: args?.presentationID || args?.presentation,
        section: args?.sectionID || args?.section,
        items: items,
        referal: args?.refId,
      },
    };
    if (args?.address) {
      variables.data["address"] = args?.address;
    }
    if (args?.delivery) {
      variables.data["delivery"] = { isOpted: args?.delivery?.isOpted };
    } else {
      variables.data["delivery"] = { isOpted: false };
    }
    if (args?.availableTime) {
      variables.data["availableTime"] = args?.availableTime;
    }
    if (args?.code) {
      variables = {
        ...variables,
        discountCode: args?.code,
      };
    }
    try {
      if (client) {
        let box = args?.existingBox;
        if (!box && args?.boxId) {
          // The cached pay order is only valid for the Box it was cached with.
          const cachedPayOrderId =
            typeof window !== "undefined" &&
            window?.sessionStorage &&
            window.sessionStorage.getItem("gems_current_box_id") === args.boxId
              ? window.sessionStorage.getItem("gems_current_pay_order_id")
              : null;
          const resolvedPayOrderId =
            args?.payOrderId || args?.payOrder || cachedPayOrderId;
          box = {
            id: args.boxId,
            ...(resolvedPayOrderId
              ? { payOrder: { id: resolvedPayOrderId } }
              : {}),
          };
        }

        let res: any = null;
        if (!box) {
          res = await client.mutate({
            mutation: CREATE_BOX,
            variables: variables,
            fetchPolicy: "no-cache",
          });
          box = res?.data?.createBox;
          // Remember the new Box now: if the ticket hold below fails (e.g. SOLD_OUT
          // when another buyer took the last ticket), the retry reuses this Box
          // instead of creating a second one.
          try {
            (
              VApplication.getInstance().getSync(
                "services.CostumePageService"
              ) as any
            )?.setCurrentBox(box);
          } catch {
            // service not registered: nothing to remember
          }
        }

        const resolvedBoxId =
          box?.id ||
          box?._id ||
          box?.createBox?.id ||
          res?.data?.createBox?.id;

        if (box && (args?.onlyCreateBox || args?.skipPaymentDispatch)) {
          return {
            ...box,
            id: resolvedBoxId || box?.id,
          };
        }

        try {
          const _res = await client.query({
            query: GET_CURRENCY_EXCHANGE_RATE,
            fetchPolicy: "network-only",
          });

          if (_res?.data?.getCurrencyExchangeRate) {
            storageService.set(
              "currencyExchangeLocal",
              _res?.data?.getCurrencyExchangeRate,
              "local",
            );
            storageService.set(
              "currencyExchangeSession",
              _res?.data?.getCurrencyExchangeRate,
              "session",
            );
          }
        } catch (exErr) {
          console.warn("GET_CURRENCY_EXCHANGE_RATE warning:", exErr);
        }

        console.log(box, is.existy(box));
        if (box && is.existy(box)) {
          console.log("hii");
          if (args?.mode === "online") {
            const storedTicketOrderStr =
              typeof window !== "undefined" && window?.sessionStorage
                ? window.sessionStorage.getItem("gems_active_ticket_order")
                : null;
            let activeTicketOrder: any = null;
            if (storedTicketOrderStr) {
              try {
                activeTicketOrder = JSON.parse(storedTicketOrderStr);
              } catch (e) {
                activeTicketOrder = null;
              }
            }
            if (!activeTicketOrder && args?.ticketOrderId) {
              activeTicketOrder = {
                id: args.ticketOrderId,
                ticketOrderId: args.ticketOrderId,
              };
            }
            if (!activeTicketOrder) {
              const cachedTicketOrderId =
                typeof window !== "undefined" && window?.sessionStorage
                  ? window.sessionStorage.getItem("gems_ticket_order_id") ||
                    window.sessionStorage.getItem("gems_ticket_orderId")
                  : null;
              if (cachedTicketOrderId) {
                activeTicketOrder = {
                  id: cachedTicketOrderId,
                  ticketOrderId: cachedTicketOrderId,
                };
              }
            }

            // Only reuse a ticket order that belongs to this Box. An id cached from an
            // earlier checkout (no boxId) must not be paid for on a new Box.
            if (
              activeTicketOrder &&
              (!activeTicketOrder.boxId ||
                !resolvedBoxId ||
                activeTicketOrder.boxId !== resolvedBoxId)
            ) {
              activeTicketOrder = null;
            }
            if (
              activeTicketOrder?.holdExpiresAt &&
              new Date(activeTicketOrder.holdExpiresAt).getTime() <= Date.now()
            ) {
              activeTicketOrder = null;
            }

            // If tickets exist on box but have not been held yet, hold them immediately after createBox
            if (!activeTicketOrder) {
              activeTicketOrder =
                (await holdSelectedTickets(client, resolvedBoxId, args)) ||
                activeTicketOrder;
            }

            if (
              storageService.get("currencyExchangeLocal", "local")?.provider ===
              "FAC"
            ) {
              const allocations = (args?.allocations || [])
                .filter(
                  (alloc: any) =>
                    !alloc?.isTicket &&
                    !String(alloc?.costumeValueId || "").startsWith("ticket_")
                )
                .map((alloc: any) => ({
                  costumeValueId: alloc.costumeValueId,
                  amount: parseFloat(alloc.amount),
                }));

              const costumeAmount = allocations.reduce(
                (sum: number, alloc: any) => sum + (alloc.amount || 0),
                0
              );

              // Use payForTickets for tickets only.
              // Use payCostumeAndTickets for a costume amount plus the tickets in one charge.
              const isTicketsOnly =
                args?.isTicketsOnly === true ||
                args?.ticketsOnly === true ||
                (costumeAmount <= 0 &&
                  (!args?.amount ||
                    parseFloat(args?.amount) <= 0 ||
                    allocations.length === 0));

              const resolvedTicketOrderId =
                activeTicketOrder?.ticketOrderId || (activeTicketOrder as any)?.id;

              if (
                activeTicketOrder?.holdExpiresAt &&
                new Date(activeTicketOrder.holdExpiresAt).getTime() <= Date.now()
              ) {
                throw new Error(
                  "HOLD_EXPIRED: Your ticket reservation has expired. Please re-reserve your tickets."
                );
              }

              const payOrderId =
                box?.payOrder?.id ||
                (typeof box?.payOrder === "string" ? box.payOrder : null) ||
                args?.payOrderId ||
                args?.payOrder ||
                (typeof window !== "undefined" && window?.sessionStorage
                  ? window.sessionStorage.getItem("gems_current_pay_order_id")
                  : null) ||
                res?.data?.createBox?.payOrder?.id;

              let resTicketPay: any = null;
              let payResult: any = null;

              if (isTicketsOnly) {
                const ticketVariables: any = {
                  data: {
                    ticketOrderId: resolvedTicketOrderId,
                    cardDetail: {
                      cardPan: (args?.number || "").replace(/\s+/g, ""),
                      cardExp: args?.expiry,
                      cardHolderName: args?.name,
                    },
                    email: args?.myEmail || args?.email || "",
                    phoneNumber: args?.phone || args?.event?.phone || "",
                  },
                };

                resTicketPay = await client.mutate({
                  mutation: PAY_FOR_TICKETS,
                  variables: ticketVariables,
                  fetchPolicy: "no-cache",
                });
                payResult = resTicketPay?.data?.payForTickets;
              } else {
                const bundledVariables: any = {
                  data: {
                    ...(resolvedTicketOrderId
                      ? { ticketOrderId: resolvedTicketOrderId }
                      : {}),
                    payOrder: payOrderId,
                    totalAmount: resolvedTicketOrderId
                      ? (costumeAmount > 0 ? costumeAmount : parseFloat(args?.amount || 0))
                      : parseFloat(args?.amount || 0),
                    platformFee: args?.platformFee
                      ? parseFloat(args?.platformFee as any)
                      : 0,
                    currency: args?.currency || "TTD",
                    allocations: allocations,
                    cardDetail: {
                      cardPan: (args?.number || "").replace(/\s+/g, ""),
                      cardExp: args?.expiry,
                      cardHolderName: args?.name,
                    },
                    email: args?.myEmail || args?.email || "",
                    phoneNumber: args?.phone || args?.event?.phone || "",
                  },
                };
                if (args?.code) {
                  bundledVariables.data.discountCode = args.code;
                }

                if (resolvedTicketOrderId) {
                  resTicketPay = await client.mutate({
                    mutation: PAY_COSTUME_AND_TICKETS,
                    variables: bundledVariables,
                    fetchPolicy: "no-cache",
                  });
                  payResult = resTicketPay?.data?.payCostumeAndTickets;
                } else {
                  // No tickets (e.g. Skip): the existing costume checkout, unchanged.
                  resTicketPay = await client.mutate({
                    mutation: CHECK_PAYMENT_FROM_FAC,
                    variables: bundledVariables,
                    fetchPolicy: "no-cache",
                  });
                  payResult = {
                    session: resTicketPay?.data?.createCheckoutFAC,
                  };
                }
              }

              if (
                payResult?.txId &&
                typeof window !== "undefined" &&
                window?.sessionStorage
              ) {
                window.sessionStorage.setItem("gems_ticket_txId", payResult.txId);
                if (resolvedTicketOrderId) {
                  window.sessionStorage.setItem(
                    "gems_ticket_orderId",
                    resolvedTicketOrderId
                  );
                }
              }

              // Both return a FAC session: handle it exactly like the value createCheckoutFAC returns today (same 3-D Secure screen)
              let redirectData = payResult?.session;
              if (typeof redirectData === "string") {
                try {
                  const parsed = JSON.parse(redirectData);
                  if (parsed?.RedirectData) {
                    redirectData = parsed.RedirectData;
                  }
                } catch (e) {
                  // Raw string HTML or URL
                }
              } else if (redirectData?.RedirectData) {
                redirectData = redirectData.RedirectData;
              }

              const facResponse = {
                ...payResult,
                RedirectData: redirectData,
                session: redirectData,
                createCheckoutFAC: {
                  RedirectData: redirectData,
                },
                payOrder: payOrderId,
                boxId: box?.id || res?.data?.createBox?.id,
                ...(resolvedTicketOrderId
                  ? { ticketOrderId: resolvedTicketOrderId }
                  : {}),
              };

              if (is.existy(redirectData)) {
                const redirectStr = String(redirectData);
                if (
                  redirectStr.startsWith("http://") ||
                  redirectStr.startsWith("https://")
                ) {
                  window.location.href = redirectStr;
                } else {
                  document.write(redirectStr);
                }
                return facResponse;
              } else if (args?.code) {
                return box;
              } else if (payResult) {
                return facResponse;
              } else {
                throw new Error(
                  `Some Error Occurred during ${
                    isTicketsOnly ? "ticket" : "costume and ticket"
                  } payment`
                );
              }
            }
            if (
              storageService.get("currencyExchangeLocal", "local")?.provider ===
                "CAL" ||
              storageService.get("currencyExchangeLocal", "local")?.provider ===
                "CALKIDS"
            ) {
              let variables: any = {
                data: {
                  currency: args?.currency,
                  amount: args?.amount
                    ? parseFloat(args?.amount)
                    : args?.price
                    ? parseFloat(args?.price)
                    : 0,
                  payorder: (box || res?.data?.createBox)?.payOrder?.id,
                },
              };
              if (args?.code) {
                variables = {
                  ...variables,
                  discountCode: args?.code,
                };
              }
              console.log("variables", variables);
              const resCal = await client.mutate({
                mutation: REGISTER_PAYMENT,
                variables: variables,
                fetchPolicy: "no-cache",
              });

              if (is.existy(resCal?.data?.registerPayment)) {
                const paymentUrl =
                  resCal?.data?.registerPayment?.data?.session[
                    "payment-hosted-url"
                  ];
                if (paymentUrl) {
                  // Redirect to the payment URL
                  window.location.href = paymentUrl;
                  console.log(`Redirecting to: ${paymentUrl}`);
                  return {
                    ...resCal?.data?.registerPayment,
                    redirection: true,
                  };
                }
                return {
                  ...resCal?.data?.registerPayment,
                  redirection: false,
                };
              } else if (args?.code) {
                return box || res?.data?.createBox;
              } else {
                throw new Error("Some Error Occurred");
              }
            } else {
              let variable: any = {
                data: {
                  name: args?.name,
                  email: args?.email,
                  payOrder: (box || res?.data?.createBox)?.payOrder?.id,
                  price: args?.amount
                    ? parseFloat(args?.amount)
                    : args?.price
                    ? parseFloat(args?.price)
                    : 0,
                  currency: args?.currency,
                  // TEMP: Platform Fee disabled until backend deployment
                  platformFee: args?.platformFee
                    ? parseFloat(args?.platformFee as any)
                    : 0,
                },
              };
              // if (args?.code) {
              //   variable = { ...variable, discountCode: args?.code }
              // }
              const orderRes = await client.mutate({
                mutation: CREATE_ORDER,
                variables: variable,
                fetchPolicy: "no-cache",
              });
              if (
                is.existy(orderRes?.data?.getAnglPayRedirectURL?.redirectUrl)
              ) {
                const output =
                  orderRes?.data?.getAnglPayRedirectURL?.redirectUrl;
                window.location.href = output;

                return orderRes?.data?.getAnglPayRedirectURL;
              } else if (
                is.existy(orderRes?.data?.getAnglPayRedirectURL?.token) ||
                is.existy(
                  orderRes?.data?.getAnglPayRedirectURL?.captureContext,
                ) ||
                is.existy(
                  orderRes?.data?.getAnglPayRedirectURL?.sessionId?.token,
                )
              ) {
                const token =
                  orderRes?.data?.getAnglPayRedirectURL?.token ||
                  orderRes?.data?.getAnglPayRedirectURL?.captureContext ||
                  orderRes?.data?.getAnglPayRedirectURL?.sessionId?.token;
                const sessionId =
                  orderRes?.data?.getAnglPayRedirectURL?.sessionId;
                return {
                  redirect: "cybersource",
                  token: token,
                  sessionId,
                  payOrder: (box || res?.data?.createBox)?.payOrder?.id,
                  data: {
                    price: args?.amount
                      ? parseFloat(args?.amount)
                      : args?.price
                      ? parseFloat(args?.price)
                      : 0,
                    platformFee: args?.platformFee
                      ? parseFloat(args?.platformFee as any)
                      : 0,
                    currency: args?.currency,
                    payOrder: (box || res?.data?.createBox)?.payOrder?.id,
                    ...(args?.allocations && { allocations: args.allocations }),
                  },
                };
              } else if (
                is.existy(
                  orderRes?.data?.getAnglPayRedirectURL?.sessionId?.sessionId,
                )
              ) {
                const output =
                  orderRes?.data?.getAnglPayRedirectURL?.sessionId?.sessionId;
                console.log(output);
                return {
                  redirect: "apv2",
                  sessionId: output,
                  platformFee: args?.platformFee,
                  data: {
                    price: args?.amount
                      ? parseFloat(args?.amount)
                      : args?.price
                      ? parseFloat(args?.price)
                      : 0,
                    currency: args?.currency,
                    payOrder: (box || res?.data?.createBox)?.payOrder?.id,
                    platformFee: args?.platformFee
                      ? parseFloat(args?.platformFee as any)
                      : 0,
                    ...(args?.allocations && { allocations: args.allocations }),
                  },
                };
              } else {
                throw new Error("Some Error Occurred");
              }
            }
          } else {
            // Pay at Camp: hold the chosen tickets on this Box as well, so they can be
            // paid later on the Orders screen (Pay Online) or by staff (Pay Offline).
            if (args?.mode === "mascamp") {
              await holdSelectedTickets(client, resolvedBoxId, args);
            }
            // Success feedback is shown via ShopUpsellHost modal on Orders
            return box || res?.data?.createBox;
          }
        } else {
          return box || res?.data?.createBox;
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      // Check for structured ticket errors (e.g. HOLD_EXPIRED, SOLD_OUT)
      const parsed = parseTicketError(err);
      if (parsed.code) {
        throw Error(parsed.rawMessage || parsed.message);
      }

      throw err;
    }
  }
}
